import {
  CONST_DELEGATE_SCHEDULING_NO_STEP_STATUSES,
  CONST_PROJECT_ARCHIVE_REASON,
  CONST_PROJECT_STATUS_LIST,
  MEETINGS_TAB_STATUS_FILTERS_BASE,
} from "../../constants";
import {
  ENUM_MEETING_STATUS,
  ENUM_PROJECT_ARCHIVE_REASON,
  ENUM_PROJECT_STATUS,
  ENUM_PROJECT_STATUS_CATEGORY,
  ENUM_USER_ROLE,
} from "../../enums";
import { store } from "../../store";
import type { QueryClient } from "@tanstack/react-query";

export const getMeetingsTabRejectedFilterStatus = (
  role: string | undefined
): ENUM_PROJECT_STATUS => {
  if (role === ENUM_USER_ROLE.AMBASSADOR) {
    return ENUM_PROJECT_STATUS.REJECTED_BY_CLIENT;
  }
  if (
    role === ENUM_USER_ROLE.VENDOR ||
    role === ENUM_USER_ROLE.DELEGATE_VENDOR ||
    role === ENUM_USER_ROLE.OPPORTUNITY_VENDOR
  ) {
    return ENUM_PROJECT_STATUS.REJECTED_BY_AMBASSADOR;
  }
  return ENUM_PROJECT_STATUS.REJECTED;
};

const getMeetingsTabStatusFilterRows = () => {
  const role = store.getState()?.auth?.user?.role;
  const rejectedStatus = getMeetingsTabRejectedFilterStatus(role);
  return [
    ...MEETINGS_TAB_STATUS_FILTERS_BASE,
    { status: rejectedStatus },
  ];
};

export const getEffectiveProjectState = (project) => {
  if (!project) return { effectiveStatus: null, effectiveCategory: null };
  if (project?.secondaryStatus) {
    return {
      effectiveStatus: ENUM_PROJECT_STATUS.ARCHIVE,
      effectiveCategory: ENUM_PROJECT_STATUS_CATEGORY.PAST,
    };
  }
  return {
    effectiveStatus: project?.status,
    effectiveCategory: CONST_PROJECT_STATUS_LIST[project?.status]?.category,
  };
};

export const getProjectStatusLabel = (status, secondaryStatus = null) => {
  const userData = store.getState()?.auth?.user;

  if (secondaryStatus) {
    return CONST_PROJECT_ARCHIVE_REASON[ENUM_PROJECT_ARCHIVE_REASON[secondaryStatus.toUpperCase()]];
  }

  const statusLabel = CONST_PROJECT_STATUS_LIST[status]?.label;

  if (typeof statusLabel === "object") {
    return (
      statusLabel?.[userData?.role] ??
      Object.values(statusLabel).find((value) => Boolean(value)) ??
      ""
    );
  }

  return statusLabel;
};


export const getProjectStatusConfig = (projectOrStatus, secondaryStatusParam = null) => {
  const isProjectObject = typeof projectOrStatus === "object";
  const projectRef = isProjectObject ? projectOrStatus : null;
  const status = isProjectObject ? projectRef?.status : projectOrStatus;
  const secondaryStatus = isProjectObject ? projectRef?.secondaryStatus : secondaryStatusParam;

  const effectiveStatus = isProjectObject
    ? getEffectiveProjectState(projectRef).effectiveStatus
    : projectOrStatus;

  const hasSecondaryStatus = Boolean(secondaryStatus);
  const baseConfig = hasSecondaryStatus
    ? { ...(CONST_PROJECT_STATUS_LIST[ENUM_PROJECT_STATUS.ARCHIVE] || {}), step: undefined }
    : { ...(CONST_PROJECT_STATUS_LIST[effectiveStatus] || {}) };

  const displayLabel = getProjectStatusLabel(status, secondaryStatus);

  return { ...baseConfig, label: displayLabel };
};

export const getInvitationStatus = (data) => {
  return data?.status;
};

export const checkAndGetInvitationStatus = (data) => {
  const userData = store.getState()?.auth?.user;

  if (data?.status !== ENUM_PROJECT_STATUS.DONE) {
    return data?.status;
  }

  if ((data?.status === ENUM_PROJECT_STATUS.DONE) === data?.isCompleted) {
    return ENUM_PROJECT_STATUS.DONE;
  }

  return data?.status === ENUM_PROJECT_STATUS.DONE &&
    ((ENUM_USER_ROLE.AMBASSADOR === userData?.role &&
      !data?.isDoneByAmbassador) ||
      (ENUM_USER_ROLE.VENDOR === userData?.role && !data?.isDoneByClient))
    ? ENUM_PROJECT_STATUS.SEND_MEETING_LINK
    : ENUM_MEETING_STATUS.DONE;
};


export const isCancelledOrRejected = (projectOrStatus, secondaryStatus = null) => {
  const { effectiveStatus } =
    typeof projectOrStatus === "object"
      ? getEffectiveProjectState(projectOrStatus)
      : { effectiveStatus: projectOrStatus };
  const hasSecondary = typeof projectOrStatus === "object" ? projectOrStatus?.secondaryStatus : secondaryStatus;
  if (hasSecondary) return true;
  return Boolean(CONST_PROJECT_STATUS_LIST[effectiveStatus]?.isClosedStatus);
};

export const isMeetingMarkAsDone = (status) => {
  const doneStatuses = [ENUM_PROJECT_STATUS.DONE];

  return doneStatuses.includes(status);
};

export const getDelegateEmails = (
  delegateData?: Array<{ email?: string }> | null
) =>
  (delegateData || [])
    .map((item) => item?.email?.trim())
    .filter((email): email is string => Boolean(email));

export const hasDelegateData = (
  delegateData?: Array<{ email?: string }> | null
) => getDelegateEmails(delegateData).length > 0;

export const getDelegateSchedulingButtonLabel = (
  delegateData?: Array<{ email?: string }> | null
) =>
  hasDelegateData(delegateData) ? "Manage Delegates" : "Delegate Scheduling";

export const shouldShowDelegateSchedulingButton = (project) => {
  if (!project) return false;
  if (isCancelledOrRejected(project)) return false;
  if (isMeetingMarkAsDone(project?.status)) return false;

  const { effectiveStatus } = getEffectiveProjectState(project);
  if (!effectiveStatus) return false;

  if (effectiveStatus === ENUM_PROJECT_STATUS.SEND_MEETING_LINK) {
    return false;
  }

  if (CONST_DELEGATE_SCHEDULING_NO_STEP_STATUSES.includes(effectiveStatus)) {
    return true;
  }

  const approvedStep =
    CONST_PROJECT_STATUS_LIST[ENUM_PROJECT_STATUS.APPROVED_BY_CLIENT]?.step ?? 3;
  const sendMeetingLinkStep =
    CONST_PROJECT_STATUS_LIST[ENUM_PROJECT_STATUS.SEND_MEETING_LINK]?.step ?? 5;
  const step = CONST_PROJECT_STATUS_LIST[effectiveStatus]?.step;

  if (step == null) return false;

  return step >= approvedStep && step < sendMeetingLinkStep;
};

export const filterProjectsByCategory = (
  projects: any[],
  category: ENUM_PROJECT_STATUS_CATEGORY
) => {
  if (!Array.isArray(projects)) return projects;
  if (category === ENUM_PROJECT_STATUS_CATEGORY.ALL) return projects;

  return projects.filter((project) => {
    const { effectiveCategory } = getEffectiveProjectState(project);
    return effectiveCategory === category;
  });
};

export const getMeetingsTabStatusFilterOptions = (projects: any[]) => {
  const counts: Record<string, number> = {};
  (projects || []).forEach((p) => {
    const s = checkAndGetInvitationStatus(p);
    if (s) counts[s] = (counts[s] || 0) + 1;
  });

  return getMeetingsTabStatusFilterRows().map(({ status, label: labelOverride }) => {
    const rawLabel = labelOverride ?? getProjectStatusLabel(status);
    const fallback =
      typeof CONST_PROJECT_STATUS_LIST[status]?.label === "string"
        ? (CONST_PROJECT_STATUS_LIST[status].label as string)
        : status;
    const label = rawLabel ?? fallback;
    return {
      value: status,
      label,
      count: counts[status] ?? 0,
    };
  });
};

export const invalidateMeetingsOverviewQueries = (
  queryClient: QueryClient,
  role?: string
) => {
  if (role === ENUM_USER_ROLE.AMBASSADOR) {
    queryClient.invalidateQueries({ queryKey: ["ambassador", "projects"] });
  } else if (
    role === ENUM_USER_ROLE.VENDOR ||
    role === ENUM_USER_ROLE.DELEGATE_VENDOR
  ) {
    queryClient.invalidateQueries({ queryKey: ["vendor", "projects"] });
  }

  queryClient.invalidateQueries({
    queryKey: ["meetings", "invitation-status-counts"],
  });
  queryClient.invalidateQueries({ queryKey: ["meetings", "invitations"] });
  queryClient.invalidateQueries({ queryKey: ["meetings", "summary", role] });
  queryClient.invalidateQueries({ queryKey: ["notifications", "upcoming-meetings"] });
};
